/**
 * AddRetailOptionDialog
 *
 * One-shot URL paste flow for the "Add retail option" button on the
 * quote editor.
 *
 * Steps:
 *   1. Tech pastes a retailer URL (Officeworks, JB Hi-Fi, Bing Lee, …)
 *   2. We POST to /api/retail-options/preview — server fetches + parses
 *   3. Dialog shows the extracted description / price / specs / image
 *      with editable fields (description + buyPrice + sellPrice are
 *      pre-filled; tech can tweak before committing)
 *   4. Click "Add to quote" → dialog closes, parent receives a
 *      `RetailOptionPreviewResponse` + tech overrides and creates a
 *      new QuoteOption
 *
 * Soft failures (`blocked` / `failed`) still show the dialog so the
 * tech can fill in fields manually — the URL is always at least
 * recorded, even if we couldn't extract anything else.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  ProductCodeOption,
  RetailOptionCommitResponse,
  RetailOptionPreviewResponse,
} from '@techgitter/contracts';
import { PRODUCT_CODE_DEFAULTS } from '@techgitter/contracts';
import {
  commitRetailOption,
  previewRetailOption,
} from '../../services/retailOptionsApi';
import { useAppSettings } from '../../hooks/useAppSettings';
import { formatSpecSummary } from '../../utils/quoteOptionSpecs';

interface AddRetailOptionDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fired AFTER the dialog has persisted the listing via the commit
   * endpoint. Carries the parsed preview, the user-edited values,
   * AND the listing back-pointers (`distiId` / `distiSku`) the
   * parent stamps on the new QuoteOption so the picker can re-find
   * the row later.
   */
  onPick: (
    preview: RetailOptionPreviewResponse,
    overrides: {
      description: string;
      buyPriceExGst: number;
      sellPriceExGst: number;
      deviceType: string;
      productCode: string;
    },
    commit: RetailOptionCommitResponse,
  ) => void;
}

const formatCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/** Render a flat list of product codes as <MenuItem>s, inserting one
 *  <ListSubheader> per distinct group in the order groups first appear.
 *  Ungrouped items render without a heading at their position in the list.
 *
 *  Only entries with a `deviceType` are surfaced — retail options are
 *  always device-class hardware, so we filter out service / software
 *  rows that the same catalog also feeds the disti picker with. */
const renderProductCodeMenuItems = (codes: ProductCodeOption[]): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastGroup: string | undefined = undefined;
  codes
    .filter((opt) => (opt.deviceType ?? '').trim() !== '')
    .forEach((opt, i) => {
      const group = opt.group?.trim() || undefined;
      if (group && group !== lastGroup) {
        nodes.push(
          <ListSubheader key={`group-${i}-${group}`}>{group}</ListSubheader>,
        );
        lastGroup = group;
      } else if (!group) {
        lastGroup = undefined;
      }
      nodes.push(
        <MenuItem key={`code-${i}-${opt.code}`} value={opt.code}>
          {opt.label}
        </MenuItem>,
      );
    });
  return nodes;
};

export const AddRetailOptionDialog: React.FC<AddRetailOptionDialogProps> = ({
  open,
  onClose,
  onPick,
}) => {
  // Settings-driven catalog with a safe fallback to the built-in
  // defaults so the dialog stays usable even on a cold cache or a
  // failed fetch. Each entry carries an optional `deviceType` — the
  // retail dialog only shows entries that have one set and stamps
  // both `code` and `deviceType` on the new QuoteOption, so picking
  // here determines both stored values from a single catalog.
  const { settings } = useAppSettings();
  const productCodeOptions: ProductCodeOption[] =
    settings?.productCodes ?? PRODUCT_CODE_DEFAULTS;
  // Two-phase state: first the URL is entered + previewed, then the
  // editable form fields go live. Keeping `preview === null` collapses
  // the UI back to just the URL input.
  const [url, setUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RetailOptionPreviewResponse | null>(null);

  // Editable fields seeded from the preview. The tech can tweak any
  // of these before clicking "Add to quote". Buy/Sell are entered in
  // inc-GST (matches what techs see on retailer pages) and converted
  // to ex-GST at submit time for the API contract.
  const [description, setDescription] = useState('');
  const [buyPriceIncGst, setBuyPriceIncGst] = useState('');
  const [sellPriceIncGst, setSellPriceIncGst] = useState('');
  // Selected product-code entry (stored as the catalog `code`).
  // Pre-filled from the server's auto-classification when one rule
  // matched. Empty when the server returned null; the tech must pick
  // before commit. The picked entry's `deviceType` is sent alongside
  // the `code` on commit so the listing/QuoteOption carry both.
  const [productCode, setProductCode] = useState('');

  // In-flight indicator while the commit endpoint persists the
  // listing. Disables the Add button so we don't fire it twice.
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  // Reset everything when the dialog reopens — otherwise stale state
  // from a previous session leaks through.
  React.useEffect(() => {
    if (open) {
      setUrl('');
      setPreviewLoading(false);
      setPreviewError(null);
      setPreview(null);
      setDescription('');
      setBuyPriceIncGst('');
      setSellPriceIncGst('');
      setProductCode('');
      setCommitting(false);
      setCommitError(null);
    }
  }, [open]);

  const handlePreview = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await previewRetailOption({ url: trimmed });
      setPreview(result);
      setDescription(result.description ?? '');
      // Seed buy + sell to the inc-GST retailer price (zero margin
      // default). Tech adjusts manually if they want a markup.
      const inc = result.priceIncGst ?? 0;
      setBuyPriceIncGst(inc > 0 ? String(inc) : '');
      setSellPriceIncGst(inc > 0 ? String(inc) : '');
      // Pre-fill from the server's classification. Prefer the
      // server-returned `productCode` (most direct signal); fall
      // back to a deviceType-based reverse lookup against the
      // catalog when only `deviceType` is set. When neither
      // resolves to a configured entry, the Select stays empty and
      // the tech is forced to pick before commit.
      const initialCode =
        (result.productCode &&
          productCodeOptions.find((o) => o.code === result.productCode)?.code) ??
        (result.deviceType &&
          productCodeOptions.find((o) => o.deviceType === result.deviceType)?.code) ??
        '';
      setProductCode(initialCode);
    } catch (err) {
      setPreviewError(
        err instanceof Error ? err.message : 'Failed to fetch retailer page',
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!preview || committing) return;
    const buyInc = Number(buyPriceIncGst);
    const sellInc = Number(sellPriceIncGst);
    if (!Number.isFinite(buyInc) || !Number.isFinite(sellInc)) return;
    // The form takes inc-GST (matches retailer pricing). The API and
    // downstream QuoteOption contract are ex-GST, so convert here.
    const toExGst = (inc: number) => Math.round((inc / 1.1) * 100) / 100;
    const buy = toExGst(buyInc);
    const sell = toExGst(sellInc);
    const finalDescription = description.trim() || preview.description || '';
    // Resolve the picked catalog entry to also pull its deviceType.
    // Guards against the picked code disappearing from the catalog
    // mid-session (the `canAdd` check should have prevented it, but
    // belt-and-braces).
    const picked = productCodeOptions.find((o) => o.code === productCode);
    if (!picked) return;
    const deviceType = (picked.deviceType ?? '').trim();

    setCommitting(true);
    setCommitError(null);
    try {
      // Persist the listing under the per-retailer vendor row. The
      // server auto-creates the vendor on first use; subsequent
      // commits for the same retailer + URL update the existing
      // listing in place (alt-key dedupe).
      const commitResp = await commitRetailOption({
        url: preview.url,
        retailerName: preview.retailerName,
        description: finalDescription,
        buyPriceExGst: buy,
        sellPriceExGst: sell,
        priceIncGst: preview.priceIncGst,
        priceExGst: preview.priceExGst,
        imageUrl: preview.imageUrl,
        manufacturerCode: preview.manufacturerCode,
        productCode,
        deviceType: deviceType || null,
        specs: preview.specs,
      });
      onPick(
        preview,
        {
          description: finalDescription,
          buyPriceExGst: buy,
          sellPriceExGst: sell,
          deviceType,
          productCode,
        },
        commitResp,
      );
    } catch (err) {
      setCommitError(
        err instanceof Error
          ? err.message
          : 'Failed to save retail listing',
      );
    } finally {
      setCommitting(false);
    }
  };

  const buyValid =
    buyPriceIncGst.trim() !== '' &&
    Number.isFinite(Number(buyPriceIncGst)) &&
    Number(buyPriceIncGst) >= 0;
  const sellValid =
    sellPriceIncGst.trim() !== '' &&
    Number.isFinite(Number(sellPriceIncGst)) &&
    Number(sellPriceIncGst) >= 0;
  const canAdd =
    preview !== null &&
    description.trim() !== '' &&
    buyValid &&
    sellValid &&
    productCode !== '' &&
    !committing;

  const status = preview?.status;
  const specSummary = preview ? formatSpecSummary(preview.specs) : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add retail option</DialogTitle>
      <DialogContent dividers>
        {/* URL input + preview button — always visible. */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Retailer URL"
            placeholder="https://www.officeworks.com.au/shop/officeworks/p/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !previewLoading && url.trim()) {
                e.preventDefault();
                handlePreview();
              }
            }}
            disabled={previewLoading}
          />
          <Button
            variant="outlined"
            disabled={previewLoading || !url.trim()}
            onClick={handlePreview}
            startIcon={
              previewLoading ? <CircularProgress size={14} /> : undefined
            }
          >
            {previewLoading ? 'Fetching…' : 'Preview'}
          </Button>
        </Stack>

        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        )}

        {preview && (
          <>
            {/* Soft-failure banner — the tech can still add the row
                manually with whatever fields are populated. */}
            {status === 'blocked' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Couldn't reach {preview.retailerName ?? 'the retailer'}{' '}
                ({preview.error}) — fill in the fields below manually.
              </Alert>
            )}
            {status === 'failed' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Page fetched but no specs / price found. Fill in the
                fields below manually.
              </Alert>
            )}

            {/* Image + retailer + spec summary header band. */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {preview.imageUrl && (
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: 100,
                    height: 100,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    component="img"
                    src={preview.imageUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {preview.retailerName && (
                    <Chip
                      label={preview.retailerName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {preview.priceIncGst != null && (
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(preview.priceIncGst)} inc-GST
                    </Typography>
                  )}
                  {preview.manufacturerCode && (
                    <Typography variant="caption" color="text.secondary">
                      MPN: {preview.manufacturerCode}
                    </Typography>
                  )}
                </Stack>
                {specSummary && (
                  <Typography variant="caption" color="text.secondary">
                    {specSummary}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Editable description — pre-filled with the calculated
                customer-facing line. */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />

            {/* Device type — pre-filled from the server's
                auto-classification when one rule matched. Backed by
                the product-code catalog; picking here stamps both
                `productCode` and `deviceType` on the new option.
                Empty + required when sparse specs prevented
                derivation. */}
            <TextField
              select
              label="Device type"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
              fullWidth
              size="small"
              required
              error={productCode === ''}
              helperText={
                preview.deviceType == null &&
                preview.productCode == null &&
                productCode === ''
                  ? "Couldn't auto-detect — pick one"
                  : ' '
              }
              sx={{ mb: 2 }}
            >
              {renderProductCodeMenuItems(productCodeOptions)}
            </TextField>

            {/* Price inputs — inc-GST to match retailer pricing. Both
                pre-filled with the listed retailer price (zero margin
                default); converted to ex-GST at submit time. */}
            <Stack direction="row" spacing={1}>
              <TextField
                label="Buy inc-GST"
                value={buyPriceIncGst}
                onChange={(e) => setBuyPriceIncGst(e.target.value)}
                inputMode="decimal"
                size="small"
                error={buyPriceIncGst.trim() !== '' && !buyValid}
                helperText={
                  preview.priceIncGst != null
                    ? `Listed: ${formatCurrency(preview.priceIncGst)} inc-GST`
                    : ' '
                }
                sx={{ flex: 1 }}
              />
              <TextField
                label="Sell inc-GST"
                value={sellPriceIncGst}
                onChange={(e) => setSellPriceIncGst(e.target.value)}
                inputMode="decimal"
                size="small"
                error={sellPriceIncGst.trim() !== '' && !sellValid}
                helperText="Defaults to buy (zero margin) — adjust for markup"
                sx={{ flex: 1 }}
              />
            </Stack>
          </>
        )}
      </DialogContent>
      {commitError && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
          {commitError}
        </Alert>
      )}
      <DialogActions>
        <Button onClick={onClose} disabled={committing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canAdd}
          onClick={handleAdd}
          startIcon={committing ? <CircularProgress size={14} /> : undefined}
        >
          {committing ? 'Saving…' : 'Add to quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
